import React, { useRef, useState, useLayoutEffect } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { Users, MapPin, Cake } from 'lucide-react';
import { formatLastFirst } from '../../lib/nameFormat';
import {
  format,
  parseISO,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isToday,
  getDay,
} from 'date-fns';
import { de } from 'date-fns/locale';
import { EVENT_COLORS } from './constants';

const isEventStart = (event, day) => {
  if (!event) return false;
  return format(day, 'yyyy-MM-dd') === event.start;
};

export const CalendarGrid = ({
  scrollRef,
  filteredEmployees,
  employeesByLocation,
  daysInMonth,
  currentDate,
  holidayMap,
  getSchulferienForDay,
  getEventForDay,
  birthdaysByDay = {},
  openDayDetails,
  highlightEmpId = null,
}) => {
  const theadRef = useRef(null);
  const [headerH, setHeaderH] = useState(0);

  useLayoutEffect(() => {
    if (theadRef.current) setHeaderH(theadRef.current.offsetHeight);
  }, [daysInMonth, filteredEmployees]);

  if (filteredEmployees.length === 0) {
    return (
      <div className="text-center py-16 text-muted-foreground">
        <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>Keine Mitarbeiter gefunden</p>
      </div>
    );
  }

  return (
    <div className="overflow-auto max-h-[calc(100vh-280px)]" ref={scrollRef}>
      <table className="w-full border-collapse" style={{ minWidth: `${120 + daysInMonth.length * 32}px` }}>
        <thead ref={theadRef}>
          <tr className="sticky top-0 z-20 bg-background shadow-[inset_0_-1px_0_0_hsl(var(--border))]">
            <th className="sticky left-0 z-30 bg-background border-b border-r border-border/40 px-2 sm:px-3 py-2 text-left text-xs font-semibold text-muted-foreground min-w-[120px] sm:min-w-[180px]">
              Mitarbeiter
            </th>
            {daysInMonth.map(day => {
              const dateStr = format(day, 'yyyy-MM-dd');
              const isWeekend = getDay(day) === 0 || getDay(day) === 6;
              const holiday = holidayMap[dateStr];
              const isTodayDate = isToday(day);
              const dayFerien = getSchulferienForDay(day);
              const hasFerien = dayFerien.length > 0;
              const dayBirthdays = birthdaysByDay[`${day.getDate()}-${day.getMonth() + 1}`] || [];
              const hasBirthdays = dayBirthdays.length > 0;

              return (
                <th
                  key={dateStr}
                  data-today={isTodayDate ? 'true' : undefined}
                  data-testid={`col-${dateStr}`}
                  className={`border-b border-border/40 px-0 py-1 text-center min-w-[36px] max-w-[36px] cursor-pointer transition-colors ${
                    isTodayDate ? 'bg-primary/10' :
                    holiday ? 'bg-purple-100 dark:bg-purple-900/40' :
                    hasFerien ? 'bg-amber-100/80 dark:bg-amber-900/30' :
                    isWeekend ? 'bg-secondary' : 'bg-background'
                  }`}
                  onClick={() => openDayDetails(day)}
                  title={[
                    hasFerien ? dayFerien.map(f => `${f.name} (${f.bundesland})`).join(', ') : null,
                    hasBirthdays ? `🎂 ${dayBirthdays.map(b => b.name).join(', ')}` : null,
                  ].filter(Boolean).join(' · ') || undefined}
                >
                  <div className="text-[10px] text-muted-foreground leading-tight">
                    {format(day, 'EE', { locale: de }).substring(0, 2)}
                  </div>
                  <div className={`text-xs font-semibold leading-tight ${isTodayDate ? 'bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center mx-auto' : ''}`}>
                    {format(day, 'd')}
                  </div>
                  {hasBirthdays && (
                    <Cake
                      data-testid={`birthday-marker-${dateStr}`}
                      className="h-3 w-3 text-pink-500 mx-auto mt-0.5"
                    />
                  )}
                  {hasFerien && (
                    <div className="mt-0.5 mx-auto w-5 h-1 rounded-full bg-amber-300 dark:bg-amber-600" title={dayFerien.map(f => f.name).join(', ')} />
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {Object.entries(employeesByLocation).map(([locName, emps]) => (
            <React.Fragment key={locName}>
              <tr>
                <td
                  className="sticky left-0 z-[25] bg-secondary px-2 sm:px-3 py-1.5 text-xs font-semibold text-muted-foreground border-b border-border/40 min-w-[120px] sm:min-w-[180px]"
                  style={{ top: headerH }}
                >
                  <div className="flex items-center gap-1.5">
                    <MapPin className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate">{locName}</span>
                    <span className="font-normal flex-shrink-0">({emps.length})</span>
                  </div>
                </td>
                {daysInMonth.map(day => (
                  <td
                    key={format(day, 'yyyy-MM-dd')}
                    className="sticky z-[12] bg-secondary border-b border-border/40 min-w-[36px] max-w-[36px]"
                    style={{ top: headerH }}
                  />
                ))}
              </tr>
              {emps.map(emp => {
                const isHl = highlightEmpId && emp.id === highlightEmpId;
                return (
                <tr key={emp.id} className={`group transition-colors ${isHl ? 'bg-primary/10' : 'hover:bg-secondary/10'}`} data-testid={`emp-row-${emp.id}`}>
                  <td className={`sticky left-0 z-10 transition-colors border-b border-r border-border/30 px-2 sm:px-3 py-1.5 text-sm font-medium whitespace-nowrap min-w-[120px] sm:min-w-[180px] ${isHl ? 'bg-primary/20 ring-2 ring-primary ring-inset animate-pulse' : 'bg-background group-hover:bg-secondary/10'}`}>
                    <div className="truncate max-w-[110px] sm:max-w-[170px]" title={formatLastFirst(emp)}>
                      {formatLastFirst(emp)}
                    </div>
                    {emp.department && (
                      <div className="text-[10px] text-muted-foreground truncate">{emp.department}</div>
                    )}
                  </td>
                  {daysInMonth.map(day => {
                    const dateStr = format(day, 'yyyy-MM-dd');
                    const isWeekend = getDay(day) === 0 || getDay(day) === 6;
                    const holiday = holidayMap[dateStr];
                    const isTodayDate = isToday(day);
                    const dayFerien = getSchulferienForDay(day);
                    const hasFerien = dayFerien.length > 0;
                    const event = getEventForDay(emp.id, day);
                    const eventStart = event && isEventStart(event, day);
                    const eventIsStartBeforeMonth = event && event.start < format(startOfMonth(currentDate), 'yyyy-MM-dd') && format(day, 'yyyy-MM-dd') === format(startOfMonth(currentDate), 'yyyy-MM-dd');
                    const showBar = eventStart || eventIsStartBeforeMonth;
                    const colorInfo = event ? EVENT_COLORS[event.type] : null;

                    let span = 0;
                    if (showBar && event) {
                      const monthEndStr = format(endOfMonth(currentDate), 'yyyy-MM-dd');
                      const visibleEnd = event.end > monthEndStr ? monthEndStr : event.end;
                      const visibleStart = dateStr;
                      const startD = parseISO(visibleStart);
                      const endD = parseISO(visibleEnd);
                      span = eachDayOfInterval({ start: startD, end: endD }).length;
                    }

                    return (
                      <td
                        key={dateStr}
                        className={`border-b border-border/20 px-0 py-1 text-center min-w-[36px] max-w-[36px] relative ${
                          isTodayDate ? 'bg-primary/5' :
                          holiday ? 'bg-purple-50/50 dark:bg-purple-900/10' :
                          hasFerien ? 'bg-amber-50/40 dark:bg-amber-900/5' :
                          isWeekend ? 'bg-secondary/20' : ''
                        }`}
                      >
                        {showBar && colorInfo && span > 0 ? (
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div
                                  className={`absolute top-1 left-0.5 h-[calc(100%-8px)] rounded-md ${colorInfo.bar} ${colorInfo.text} flex items-center overflow-hidden cursor-pointer`}
                                  style={{ width: `calc(${span * 100}% - 4px)`, zIndex: 5 }}
                                  data-testid={`event-bar-${emp.id}-${event.id}`}
                                >
                                  <span className="text-[10px] font-medium px-1 truncate whitespace-nowrap">
                                    {span >= 3 ? event.label : ''}
                                  </span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-[250px]">
                                <div className="text-xs space-y-0.5">
                                  <p className="font-semibold">{formatLastFirst(emp)}</p>
                                  <p>{EVENT_COLORS[event.type].label}</p>
                                  <p className="text-muted-foreground">
                                    {format(parseISO(event.start), 'dd.MM.', { locale: de })} - {format(parseISO(event.end), 'dd.MM.yyyy', { locale: de })}
                                  </p>
                                  {event.notes && <p className="text-muted-foreground italic">{event.notes}</p>}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};
